import React, { useState } from 'react';
import {
  GoogleMap,
  InfoWindow,
  Marker,
  useLoadScript,
} from '@react-google-maps/api';
import { FindhelpProgram } from '@src/api.types';
import { Heading } from '@uhg-abyss/web/ui/Heading';
import { Link } from '@uhg-abyss/web/ui/Link';
import { Text } from '@uhg-abyss/web/ui/Text';
import { IconSymbol } from '@uhg-abyss/web/ui/IconSymbol';
import { Layout } from '@uhg-abyss/web/ui/Layout';
import { UniteUsProgram } from '@repo/ui';

export type MarkerTarget = {
  id: string;
  name: string;
  distance: number | null;
  position: {
    lat: number;
    lng: number;
  };
};

export function uniteusIntoMarkerTarget(
  pgm: UniteUsProgram
): MarkerTarget | undefined {
  return {
    id: pgm.id,
    name: pgm.document.name,
    // FIXME - We don't seem to get distance currently.
    distance: null,
    position: {
      lat: pgm.document.locations[0].latitude,
      lng: pgm.document.locations[0].longitude,
    },
  };
}

// Utility to extract the map-relevant info from a findhelp program structure.
export function intoMarkerTarget(
  pgm: FindhelpProgram
): MarkerTarget | undefined {
  if (
    !pgm.offices ||
    pgm.offices.length === 0 ||
    // TODO - Reconsider if we want to always just grab the first office.
    pgm.offices[0].location === undefined
  ) {
    return undefined;
  }

  return {
    id: pgm.id,
    name: pgm.name,
    distance: pgm.offices[0].distance,
    position: {
      lat: pgm.offices[0].location.latitude,
      lng: pgm.offices[0].location.longitude,
    },
  };
}

export type CboMapProps = {
  apiKey: string;
  programSearchResults: MarkerTarget[];
  onClickViewProgramLink: (clickedProgramId: string) => void;
};

export function CboMap({
  apiKey,
  programSearchResults,
  onClickViewProgramLink,
}: CboMapProps): React.JSX.Element {
  const { isLoaded } = useLoadScript({
    googleMapsApiKey: apiKey,
  });

  const [showMarkerInfo, setShowMarkerInfo] = useState<MarkerTarget | null>(
    null
  );

  return (
    <div>
      {!isLoaded ? (
        <h1>Loading...</h1>
      ) : (
        <React.Fragment>
          {programSearchResults !== undefined ? (
            <GoogleMap
              onLoad={(map) => {
                const bounds = new google.maps.LatLngBounds();
                programSearchResults.forEach(({ position }) => {
                  return bounds.extend(position);
                });
                map.fitBounds(bounds);
              }}
              mapContainerStyle={{
                width: '100%',
                height: '50vh',
              }}
              zoom={10}
              // Default to the first result, otherwise null island.
              center={programSearchResults[0]?.position ?? { lat: 0, lng: 0 }}
            >
              {programSearchResults.map((marker: MarkerTarget) => (
                <Marker
                  data-cy={`map-marker-${marker.id}`}
                  key={marker.id}
                  position={marker.position}
                  onClick={() => {
                    setShowMarkerInfo(marker);
                    onClickViewProgramLink(marker.id);
                  }}
                >
                  {showMarkerInfo?.id === marker.id ? (
                    <InfoWindow
                      onCloseClick={() => {
                        setShowMarkerInfo(null);
                      }}
                    >
                      <div>
                        <Heading>{showMarkerInfo.name}</Heading>

                        <Layout.Group>
                          <IconSymbol icon="location_on" />
                          <Text>{showMarkerInfo.distance} miles away</Text>
                        </Layout.Group>

                        <br></br>

                        <Link
                          data-cy={`map-view-program-link-${marker.id}`}
                          isStandardAnchor={true}
                          variant="underlined"
                          onClick={() => {
                            onClickViewProgramLink(marker.id);
                          }}
                        >
                          View Program
                        </Link>
                      </div>
                    </InfoWindow>
                  ) : null}
                </Marker>
              ))}
            </GoogleMap>
          ) : (
            ''
          )}
        </React.Fragment>
      )}
    </div>
  );
}
